import classNames from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import moment from 'moment-timezone';
import type { KeyboardEvent } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { DropdownItemProps } from 'semantic-ui-react';
import {
  Button,
  Checkbox,
  Form,
  FormField,
  Icon,
  Input,
  Popup,
  Radio,
  Select,
} from 'semantic-ui-react';

import type {
  Field,
  FieldEditorProps,
  FieldEnumOptions,
  FieldType,
  FieldValidator,
} from '@models';
import { configSchemas } from '@server/config/schema';
import type { ConfigKey } from '@server/config/types';
import DatePicker from '@components/date-picker';
import DateRange from '@components/date-range';
import JSONFieldEditor from '@components/json-field-editor';
import { configPlugins } from '@components/json-field-editor/plugins/config-view';
import { KeyValueEditorDropdown } from '@components/key-value-editor';
import { createEnumOptions } from '@components/pills/field-formatter';
import { isFieldEmpty } from '@utils/common';

import s from './field-editor.scss';

const createEnumDropdownOptions = (
  values?: FieldEnumOptions,
): DropdownItemProps[] => {
  if (!values) return [];

  return createEnumOptions(values).map(({ label: text, value }) => ({
    key: `${value}_enumItem`,
    text,
    value,
  }));
};

const parseFieldValue = (value: string, typeName: FieldType) =>
  ['Int', 'BigInt'].includes(typeName)
    ? parseInt(value, 10)
    : ['Float'].includes(typeName)
      ? parseFloat(value)
      : value;

/**
 * Returns a sensible default value when enabling a nullable field.
 * Used when no previous value or example is available.
 */
const getDefaultEnabledValue = (typeName: FieldType): unknown => {
  switch (typeName) {
    case 'Int':
    case 'BigInt':
      return 1;
    case 'Float':
      return 1.0;
    default:
      return '';
  }
};

const baseValidators = {
  String: (value, options) =>
    typeof value === 'string' && options?.trimValues ? value.trim() : value,
  Int: (value, options) => {
    const { maxValue, minValue } = options ?? {};
    let numVal = typeof value === 'string' ? parseInt(value, 10) : value;
    if (typeof numVal === 'number' && !Number.isNaN(numVal)) {
      if (minValue !== undefined) numVal = Math.max(minValue, numVal);
      if (maxValue !== undefined) numVal = Math.min(maxValue, numVal);
      return numVal;
    }
    return undefined;
  },
  Float: (value, options) => {
    const { decimalPlaces, maxValue, minValue } = options ?? {};
    let numVal = typeof value === 'string' ? parseFloat(value) : value;
    if (typeof numVal === 'number' && !Number.isNaN(numVal)) {
      if (minValue !== undefined) numVal = Math.max(minValue, numVal);
      if (maxValue !== undefined) numVal = Math.min(maxValue, numVal);
      if (decimalPlaces)
        numVal = Math.round(numVal * 10 ** decimalPlaces) / 10 ** decimalPlaces;
      return numVal;
    }
    return undefined;
  },
} satisfies Partial<Record<FieldType, FieldValidator>>;

const validators: Partial<Record<FieldType, FieldValidator>> = {
  ...baseValidators,
  BigInt: baseValidators.Int,
};

const FieldEditor = ({
  field,
  disabled = false,
  selected = false,
  inputId = `field_editor_input_${field.key}`,
  label,
  onChange,
  setEditorPopupOpen = undefined,
  className,
  inputClassName,
  clearable = true,
  validateChanges,
  inputRef,
  showBooleansAsToggles,
  defaultOpen = false,
}: FieldEditorProps) => {
  const [editValue, setEditValue] = useState(field.data);

  // Store the previous value when disabling a nullable field, so it can be
  // restored when re-enabling the associated checkbox
  const previousNullableValueRef = useRef<string | number | boolean | null>(
    null,
  );

  const applyChanges = useCallback(
    (updatedField: Field) => {
      const { typeName, validationOptions } = updatedField;
      const validator = validators[typeName!];
      onChange({
        ...updatedField,
        data: validator
          ? validator(updatedField.data, validationOptions)
          : updatedField.data,
      } as Field);
    },
    [onChange],
  );

  const saveChangesBtn = useMemo(() => {
    const updatedField = { ...field, data: editValue } as Field;
    return validateChanges && !isFieldEmpty(updatedField) ? (
      <Button
        aria-label="Save changes"
        icon="check"
        className={classNames(s.saveChangesBtn, 'darkreader-ignore')}
        onClick={() => {
          applyChanges(updatedField);
        }}
      />
    ) : undefined;
  }, [validateChanges, applyChanges, editValue, field]);

  const editor = useMemo(() => {
    const {
      data,
      typeName,
      example,
      required = false,
      key,
      validationOptions,
    } = field;
    const { maxValue, minValue, step } = validationOptions ?? {};

    switch (typeName) {
      case 'Boolean':
        if (label) {
          return (
            <Checkbox
              autoFocus={selected}
              toggle={showBooleansAsToggles}
              id={inputId}
              label={label}
              checked={data ?? example ?? false}
              disabled={disabled}
              onChange={(ev, { checked }) =>
                applyChanges({
                  ...field,
                  typeName,
                  data: checked!,
                })
              }
              className={classNames(
                inputClassName,
                'darkreader-ignore',
                'darkreader-ignore-inline-style',
              )}
            />
          );
        }
        return (
          <div className={s.toggleWrapper}>
            <span
              className={classNames(
                s.toggleValue,
                s.toggleValueFalse,
                data ? null : s.toggleValueActive,
              )}
            >
              False
            </span>
            <Radio
              autoFocus={selected}
              type="radio"
              className={classNames(s.toggle, data ? s.toggleChecked : null)}
              required={required}
              toggle
              checked={data ?? false}
              disabled={disabled}
              onChange={(ev, { checked }) =>
                applyChanges({
                  ...field,
                  typeName,
                  data: checked!,
                })
              }
            />
            <span
              className={classNames(
                s.toggleValue,
                s.toggleValueTrue,
                data ? s.toggleValueActive : null,
              )}
            >
              True
            </span>
          </div>
        );
      case 'Enum': {
        const { enumValues } = field;
        return (
          <Form.Select
            id={inputId}
            autoFocus={selected}
            style={{ minWidth: '100%', lineHeight: '1.21428571em' }}
            selectOnBlur={false}
            clearable={clearable}
            disabled={disabled}
            defaultOpen={defaultOpen}
            onChange={(_event, { value }) =>
              applyChanges({
                ...field,
                typeName,
                enumValues,
                data: typeof value === 'string' ? value : '',
              })
            }
            value={data ?? example ?? ''}
            options={createEnumDropdownOptions(enumValues)}
            className={inputClassName}
            placeholder="Select a value"
          />
        );
      }
      case 'EnumList': {
        const { enumValues } = field;
        return (
          <FormField className={classNames(s.enumListDropdown, inputClassName)}>
            <Select
              id={inputId}
              autoFocus={selected}
              disabled={disabled}
              selectOnBlur={false}
              defaultOpen={defaultOpen}
              multiple
              clearable={clearable}
              onChange={(_event, { value }) => {
                if (validateChanges) {
                  setEditValue(Array.isArray(value) ? (value as string[]) : []);
                } else {
                  applyChanges({
                    ...field,
                    typeName,
                    enumValues,
                    data: Array.isArray(value) ? (value as string[]) : [],
                  });
                }
              }}
              value={
                validateChanges
                  ? (editValue as string[])
                  : (data ?? example ?? [])
              }
              options={createEnumDropdownOptions(enumValues)}
              placeholder="Select a value"
            />
            {saveChangesBtn}
          </FormField>
        );
      }
      case 'TimePeriod': {
        const num = data ? data.slice(0, -1) : '';
        const unit = data ? data.slice(-1) : '';
        return (
          <Form.Group style={{ marginBottom: 0 }}>
            <Form.Field width="6" style={{ paddingRight: 0 }}>
              <Form.Input
                id={inputId}
                autoFocus={selected}
                value={num}
                placeholder="Enter num"
                type="number"
                disabled={disabled}
                min="0"
                onChange={(_event, { value }) => {
                  const val = value.match(/^[1-9]\d*$/g)
                    ? `${value}${unit || 'w'}`
                    : undefined;
                  applyChanges({
                    ...field,
                    typeName,
                    data: val?.length ? val : undefined,
                  });
                }}
                style={{ minWidth: '6rem' }}
                className={inputClassName}
              />
            </Form.Field>
            <Form.Field width="10" style={{ height: '100%' }}>
              <Form.Select
                selectOnBlur={false}
                clearable={clearable}
                disabled={disabled}
                onChange={(_event, { value }) =>
                  applyChanges({
                    ...field,
                    typeName,
                    data: value ? `${num || '1'}${value}` : undefined,
                  })
                }
                value={unit}
                options={[
                  {
                    key: `${key}_weeks`,
                    text: `Week${num > '1' ? 's' : ''}`,
                    value: 'w',
                  },
                  {
                    key: `${key}_days`,
                    text: `Day${num > '1' ? 's' : ''}`,
                    value: 'd',
                  },
                  {
                    key: `${key}_hours`,
                    text: `Hour${num > '1' ? 's' : ''}`,
                    value: 'h',
                  },
                ]}
                placeholder="Select a value"
                style={{
                  minWidth: '10rem',
                  height: 'inherit',
                  lineHeight: '1.21428571em',
                }}
              />
            </Form.Field>
          </Form.Group>
        );
      }
      case 'DateTime':
        return (
          <DatePicker
            disabled={disabled}
            value={data ?? ''}
            setCalendarOpen={setEditorPopupOpen}
            onChange={value =>
              applyChanges({
                ...field,
                typeName,
                data: moment(value).toISOString(),
              })
            }
            buttonProps={{ size: 'tiny' }}
            className={inputClassName}
          />
        );
      case 'StringList': {
        const values = data ?? example;
        return (
          <div className={classNames(inputClassName, s.stringArrayWrapper)}>
            {values?.map?.((value, index) => {
              const input = (
                <Input
                  // eslint-disable-next-line react/no-array-index-key
                  key={`string_item_${index}`}
                  className={classNames(
                    disabled ? s.disabledInput : '',
                    s.truncatedWithEllipsis,
                  )}
                  fluid
                  autoFocus={selected && index === 0}
                  value={value}
                  onChange={(_event, { value: val }) => {
                    const newField = {
                      ...field,
                      typeName,
                      data: [
                        ...values.slice(0, index),
                        val.trim(),
                        ...values.slice(index + 1),
                      ],
                    };
                    applyChanges(newField);
                  }}
                  action={{
                    icon: 'trash',
                  }}
                >
                  <input
                    id={index === 0 ? inputId : `${inputId}-${index + 1}`}
                    type="text"
                    readOnly={disabled}
                  />
                  <Button
                    icon
                    className={s.deleteBtn}
                    tabindex="-1"
                    disabled={disabled}
                    onClick={() =>
                      applyChanges({
                        ...field,
                        typeName,
                        data: [
                          ...values.slice(0, index),
                          ...values.slice(index + 1),
                        ],
                      })
                    }
                  >
                    <Icon name="trash" />
                  </Button>
                </Input>
              );
              return disabled ? (
                <Popup on="hover" wide="very" content={value} trigger={input} />
              ) : (
                input
              );
            })}
            {!values?.length && (
              <div
                className={classNames(s.noValueSet, disabled ? s.disabled : '')}
              >
                No value set
              </div>
            )}
            {!disabled && (
              <Button
                fluid
                basic
                onClick={() => {
                  const id = values?.length
                    ? `${inputId}-${values.length + 1}`
                    : inputId;
                  applyChanges({
                    ...field,
                    typeName,
                    data: [...(values ?? []), ''],
                  });
                  setTimeout(() => document.getElementById(id)?.focus(), 0);
                }}
                disabled={disabled}
              >
                <Icon name="add" />
                Add Value
              </Button>
            )}
          </div>
        );
      }

      case 'JSON': {
        const {
          isPartiallyFromEnv,
          name,
          description,
          isDeprecated,
          deprecatedReason,
        } = field;
        // Normalize the config key by removing prefix before the first dot
        const dotIndex = field.key.indexOf('.');
        const normalizedKey =
          dotIndex !== -1 ? field.key.substring(dotIndex + 1) : field.key;

        // Get the Zod schema directly from `configSchemas`. All validation
        // logic is centralized in the base schemas.
        const zodSchema = configSchemas[normalizedKey as ConfigKey];

        // If no Zod schema is available, we can't use the JSONFieldEditor
        // This would be the case for non-config JSON fields
        if (!zodSchema) {
          // For now, return a message. In the future, we could handle generic
          // JSON editing
          return <div>JSON editing not available for this field type</div>;
        }

        return (
          <JSONFieldEditor
            data={data}
            example={example}
            disabled={disabled}
            isPartiallyFromEnv={isPartiallyFromEnv}
            inputId={inputId}
            zodSchema={zodSchema}
            lookupKey={normalizedKey}
            plugins={configPlugins}
            title={`Edit Configuration: ${name || normalizedKey}`}
            description={description}
            isDeprecated={isDeprecated}
            deprecatedReason={deprecatedReason}
            onChange={newData =>
              applyChanges({
                ...field,
                typeName,
                data: newData,
              })
            }
          />
        );
      }

      case 'DateRange': {
        return (
          <DateRange
            className={classNames(className, inputClassName)}
            value={data}
            onChange={value => applyChanges({ ...field, data: value })}
          />
        );
      }

      case 'KeyValue': {
        const { itemName } = field;

        return (
          <KeyValueEditorDropdown
            className={inputClassName}
            disabled={disabled}
            value={data}
            itemName={itemName}
            onChange={value =>
              applyChanges({
                ...field,
                typeName,
                data: value,
              })
            }
          />
        );
      }

      default: {
        const { isNullable: fieldIsNullable } = field;

        // Calculate the effective value (actual data or fall back to default /
        // example). Cast to include boolean since some nullable fields
        // (like `cacheExpiry`) can have `false` as a valid value to indicate
        // that it is disabled.
        const effectiveValue = (data ?? example ?? null) as
          string | number | boolean | null;

        // For nullable fields, determine if the field is enabled (active).
        // - A field is considered disabled if its effective value is `null`,
        //   `false`, or `0`
        // - This reflects whether the feature is actually active, not just
        //   whether there's a stored value (e.g., `cacheExpiry=null` uses
        //   default `600`, so it's active)
        const isCurrentlyEnabled = fieldIsNullable
          ? effectiveValue !== null &&
            effectiveValue !== false &&
            effectiveValue !== 0
          : true;

        // Shared Input component for both nullable and non-nullable primitives
        const primitiveInput = (
          <Input
            ref={inputRef}
            className={classNames(
              fieldIsNullable ? s.nullableInput : '',
              disabled || (fieldIsNullable && !isCurrentlyEnabled)
                ? s.disabledInput
                : '',
              inputClassName,
            )}
            action={saveChangesBtn}
            id={inputId}
            autoFocus={selected}
            disabled={disabled || (fieldIsNullable && !isCurrentlyEnabled)}
            value={
              validateChanges
                ? editValue
                : fieldIsNullable && !isCurrentlyEnabled
                  ? String(
                      previousNullableValueRef.current ?? effectiveValue ?? '',
                    )
                  : String(effectiveValue ?? '')
            }
            placeholder={
              fieldIsNullable && isCurrentlyEnabled
                ? 'Enter a value'
                : fieldIsNullable
                  ? String(example ?? '')
                  : 'Enter a value'
            }
            type={
              typeName === 'Int' ||
              typeName === 'BigInt' ||
              typeName === 'Float'
                ? 'number'
                : 'text'
            }
            min={minValue}
            max={maxValue}
            step={step}
            onKeyPress={(event: KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter') {
                applyChanges({ ...field, data: editValue } as Field);
              }
            }}
            onChange={(_event, { value }) => {
              if (validateChanges) {
                setEditValue(parseFieldValue(value, typeName!));
              } else {
                applyChanges({
                  ...field,
                  data: value,
                } as Field);
              }
            }}
            button
          />
        );

        // For nullable primitive values, show a checkbox to enable/disable
        if (fieldIsNullable) {
          return (
            <div className={s.nullablePrimitiveWrapper}>
              {primitiveInput}
              <Checkbox
                id={`${inputId}-nullable-toggle`}
                label="Enabled"
                checked={isCurrentlyEnabled}
                disabled={disabled}
                onChange={(ev, { checked }) => {
                  if (!checked) {
                    // Store the current value before disabling so it can be
                    // restored
                    previousNullableValueRef.current = data ?? example ?? null;

                    // Disable: set to `0` for numeric types (represents
                    // "disabled" state), and `null` for other types
                    const disabledValue =
                      typeName === 'Int' ||
                      typeName === 'BigInt' ||
                      typeName === 'Float'
                        ? 0
                        : null;
                    applyChanges({
                      ...field,
                      data: disabledValue,
                    } as unknown as Field);
                  } else {
                    // Enable: restore previous value if available, otherwise
                    // fall back to example / default or a sensible non-zero
                    // value
                    const hasPreviousValue =
                      previousNullableValueRef.current != null &&
                      previousNullableValueRef.current !== false &&
                      previousNullableValueRef.current !== 0;

                    const restoredData = hasPreviousValue
                      ? previousNullableValueRef.current
                      : (example ?? getDefaultEnabledValue(typeName!));

                    applyChanges({
                      ...field,
                      data: restoredData,
                    } as Field);
                  }
                }}
                className={s.nullableCheckbox}
              />
            </div>
          );
        }

        // For non-nullable primitives, show regular input
        return primitiveInput;
      }
    }
  }, [
    field,
    label,
    selected,
    disabled,
    setEditorPopupOpen,
    inputClassName,
    validateChanges,
    inputId,
    applyChanges,
    clearable,
    editValue,
    inputRef,
    saveChangesBtn,
    showBooleansAsToggles,
    className,
    defaultOpen,
  ]);

  return (
    <div className={classNames(s.root, label ? s.withLabel : '', className)}>
      {editor}
    </div>
  );
};

export default withStyles(s)(FieldEditor);
